import config from './config';

const { COGNITO_DOMAIN, COGNITO_CLIENT_ID, REDIRECT_URI } = config;

// Marks that the next return to our origin is a deliberate sign-out. Shared with
// App.js so the set/read/clear sites move together.
export const SIGNED_OUT_KEY = 'signed_out';

function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function login() {
  const verifier = generateCodeVerifier();
  sessionStorage.setItem('pkce_verifier', verifier);
  const challenge = await generateCodeChallenge(verifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: COGNITO_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: 'openid email profile',
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });

  window.location.href = `${COGNITO_DOMAIN}/login?${params}`;
}

export function logout() {
  sessionStorage.removeItem('id_token');
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  sessionStorage.removeItem('pkce_verifier');
  // Signal the signed-out landing via sessionStorage (not a query param): it is
  // per-origin and survives the round-trip through Cognito, so the returning
  // logout_uri stays exactly origin + '/' — which is already a registered
  // sign-out URL on the app client (see backend_stack.py logout_urls). Using a
  // query string instead would require registering that extra URL in Cognito.
  sessionStorage.setItem(SIGNED_OUT_KEY, '1');

  const params = new URLSearchParams({
    client_id: COGNITO_CLIENT_ID,
    logout_uri: window.location.origin + '/',
  });

  window.location.href = `${COGNITO_DOMAIN}/logout?${params}`;
}

export async function handleCallback(code) {
  const verifier = sessionStorage.getItem('pkce_verifier');
  sessionStorage.removeItem('pkce_verifier');

  const response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: COGNITO_CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      code,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    throw new Error('Token exchange failed');
  }

  const tokens = await response.json();
  sessionStorage.setItem('id_token', tokens.id_token);
  sessionStorage.setItem('access_token', tokens.access_token);
  if (tokens.refresh_token) {
    sessionStorage.setItem('refresh_token', tokens.refresh_token);
  }
  return tokens;
}

export function getToken() {
  return sessionStorage.getItem('id_token');
}

export function isAuthenticated() {
  const token = getToken();
  if (!token) return false;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export function getUserEmail() {
  const token = getToken();
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.email;
  } catch {
    return null;
  }
}

// Cognito groups from the ID token (`cognito:groups`), used only to decide
// whether to SHOW the admin UI. The real authorization is server-side
// (is_admin on the admin endpoints) — this is a convenience, not a gate.
export function getUserGroups() {
  const token = getToken();
  if (!token) return [];

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const groups = payload['cognito:groups'];
    if (Array.isArray(groups)) return groups.map(String);
    if (typeof groups === 'string') return groups.split(/[,\s]+/).filter(Boolean);
    return [];
  } catch {
    return [];
  }
}

export function isAdmin() {
  return getUserGroups().includes('admins');
}
